// features/pos/components/VoidSaleModal.tsx
// ENHANCED - Better Visuals, Step Indicator, Improved Validation, Keyboard Support

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    XCircle, AlertCircle, X, Shield, DollarSign,
    ChevronRight, ChevronLeft, CheckCircle, RotateCcw,
    FileText, Hash
} from 'lucide-react';
import { clsx } from 'clsx';

const VOID_REASONS = [
    { id: 'customer_changed', label: 'Customer changed mind', icon: '🤷' },
    { id: 'pricing_error', label: 'Pricing error', icon: '💲' },
    { id: 'duplicate', label: 'Duplicate transaction', icon: '📋' },
    { id: 'wrong_product', label: 'Wrong product scanned', icon: '❌' },
    { id: 'insufficient_funds', label: 'Customer insufficient funds', icon: '💳' },
    { id: 'technical_error', label: 'Technical error', icon: '🔧' },
    { id: 'other', label: 'Other', icon: '📝' },
];

interface VoidSaleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onVoid: (reason: string, supervisorPin?: string) => Promise<boolean>;
    saleTotal: number;
    orderNumber: string;
    requiresSupervisor: boolean;
}

export function VoidSaleModal({
    isOpen, onClose, onVoid, saleTotal, orderNumber, requiresSupervisor
}: VoidSaleModalProps) {
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [supervisorPin, setSupervisorPin] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [step, setStep] = useState<'reason' | 'pin' | 'confirm'>('reason');
    const pinInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            setReason('');
            setCustomReason('');
            setSupervisorPin('');
            setError('');
            setIsProcessing(false);
            setStep('reason');
        }
    }, [isOpen]);

    useEffect(() => {
        if (step === 'pin' && pinInputRef.current) {
            setTimeout(() => pinInputRef.current?.focus(), 200);
        }
    }, [step]);

    const formatNaira = (val: number) => {
        if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
        return `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const getFinalReason = () => {
        if (reason === 'other') return customReason;
        const selectedReason = VOID_REASONS.find(r => r.id === reason);
        return selectedReason?.label || reason;
    };

    const handleContinue = () => {
        const finalReason = getFinalReason();
        if (!finalReason.trim()) {
            setError('Please provide a void reason.');
            return;
        }

        if (requiresSupervisor) {
            setStep('pin');
            setError('');
        } else {
            setStep('confirm');
            setError('');
        }
    };

    const handleVoid = async () => {
        const finalReason = getFinalReason();
        setIsProcessing(true);
        setError('');

        const success = await onVoid(finalReason, requiresSupervisor ? supervisorPin : undefined);

        setIsProcessing(false);

        if (!success) {
            setError(requiresSupervisor ? 'Invalid supervisor PIN or void failed.' : 'Void failed. Please try again.');
            if (requiresSupervisor) {
                setSupervisorPin('');
                pinInputRef.current?.focus();
            }
        }
    };

    const handleClose = () => {
        setReason('');
        setCustomReason('');
        setSupervisorPin('');
        setError('');
        setStep('reason');
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && step === 'confirm') {
            handleVoid();
        }
        if (e.key === 'Escape') {
            if (step === 'pin' || step === 'confirm') {
                setStep('reason');
            } else {
                handleClose();
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        onKeyDown={handleKeyDown}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-br from-rose-600 to-rose-700 px-6 py-5 text-white">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-white/20">
                                        <RotateCcw size={20} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold">Void Sale</h2>
                                        <p className="text-rose-100 text-xs">Reverse transaction & restore inventory</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
                                    type="button"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Sale Info */}
                            <div className="flex items-center gap-4 px-2">
                                <div className="flex items-center gap-1.5 text-rose-100 text-xs">
                                    <FileText size={12} />
                                    <span className="font-mono">{orderNumber}</span>
                                </div>
                                <div className="w-1 h-1 rounded-full bg-rose-300" />
                                <div className="flex items-center gap-1.5 text-white text-sm font-bold">
                                    <DollarSign size={14} />
                                    {formatNaira(saleTotal)}
                                </div>
                            </div>

                            {/* Step Indicator */}
                            <div className="flex items-center gap-2 mt-4 px-2">
                                <div className={clsx(
                                    'flex items-center gap-1.5 text-xs font-medium transition-all',
                                    step === 'reason' ? 'text-white' : 'text-rose-200'
                                )}>
                                    <div className={clsx(
                                        'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                                        step === 'reason' ? 'bg-white text-rose-600' : 'bg-rose-500 text-rose-200'
                                    )}>
                                        1
                                    </div>
                                    Reason
                                </div>
                                <ChevronRight size={12} className="text-rose-300" />
                                {requiresSupervisor && (
                                    <>
                                        <div className={clsx(
                                            'flex items-center gap-1.5 text-xs font-medium transition-all',
                                            step === 'pin' ? 'text-white' : 'text-rose-200'
                                        )}>
                                            <div className={clsx(
                                                'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                                                step === 'pin' ? 'bg-white text-rose-600' : 'bg-rose-500 text-rose-200'
                                            )}>
                                                2
                                            </div>
                                            PIN
                                        </div>
                                        <ChevronRight size={12} className="text-rose-300" />
                                    </>
                                )}
                                <div className={clsx(
                                    'flex items-center gap-1.5 text-xs font-medium transition-all',
                                    step === 'confirm' ? 'text-white' : 'text-rose-200'
                                )}>
                                    <div className={clsx(
                                        'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                                        step === 'confirm' ? 'bg-white text-rose-600' : 'bg-rose-500 text-rose-200'
                                    )}>
                                        {requiresSupervisor ? 3 : 2}
                                    </div>
                                    Confirm
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            {step === 'reason' && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2 text-sm text-rose-700">
                                        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold">Warning: Permanent Action</p>
                                            <p className="text-xs mt-0.5">Voiding will restore all inventory and reverse this transaction. This cannot be undone.</p>
                                        </div>
                                    </div>

                                    {requiresSupervisor && (
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-sm text-amber-700">
                                            <Shield size={16} className="flex-shrink-0 mt-0.5" />
                                            <p>This sale exceeds ₦2,000 and requires <strong>supervisor authorization</strong>.</p>
                                        </div>
                                    )}

                                    <div>
                                        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 mb-3">
                                            <Hash size={12} />
                                            Select Void Reason *
                                        </label>
                                        <div className="grid grid-cols-1 gap-2">
                                            {VOID_REASONS.map((r) => (
                                                <motion.button
                                                    key={r.id}
                                                    whileHover={{ scale: 1.01 }}
                                                    whileTap={{ scale: 0.99 }}
                                                    onClick={() => { setReason(r.id); setError(''); }}
                                                    className={clsx(
                                                        'flex items-center gap-3 p-3 rounded-xl border-2 text-sm font-medium text-left transition-all',
                                                        reason === r.id
                                                            ? 'border-rose-400 bg-rose-50 text-rose-700 shadow-sm'
                                                            : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                                                    )}
                                                    type="button"
                                                >
                                                    <span className="text-lg">{r.icon}</span>
                                                    <span className="flex-1">{r.label}</span>
                                                    {reason === r.id && (
                                                        <CheckCircle size={16} className="text-rose-500" />
                                                    )}
                                                </motion.button>
                                            ))}
                                        </div>

                                        {reason === 'other' && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                className="mt-2"
                                            >
                                                <textarea
                                                    value={customReason}
                                                    onChange={(e) => { setCustomReason(e.target.value); setError(''); }}
                                                    rows={3}
                                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 transition-all"
                                                    placeholder="Please explain the reason for voiding this sale..."
                                                />
                                            </motion.div>
                                        )}
                                    </div>

                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700"
                                        >
                                            <AlertCircle size={16} className="flex-shrink-0" />
                                            {error}
                                        </motion.div>
                                    )}

                                    <div className="flex gap-3 pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleClose}
                                            className="flex-1 py-3.5 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all"
                                            type="button"
                                        >
                                            Cancel
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleContinue}
                                            disabled={!reason || (reason === 'other' && !customReason.trim())}
                                            className="flex-1 py-3.5 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-rose-500/25 hover:from-rose-600 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                            type="button"
                                        >
                                            {requiresSupervisor ? 'Continue' : 'Review & Confirm'}
                                            <ChevronRight size={16} />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}

                            {step === 'pin' && requiresSupervisor && (
                                <motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="text-center">
                                        <motion.div
                                            animate={{ scale: [1, 1.1, 1] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                            className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3"
                                        >
                                            <Shield size={28} className="text-amber-600" />
                                        </motion.div>
                                        <h3 className="text-lg font-bold text-slate-900">Supervisor Authorization</h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Enter supervisor PIN to void {formatNaira(saleTotal)}
                                        </p>
                                    </div>

                                    <div>
                                        <input
                                            ref={pinInputRef}
                                            type="password"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={6}
                                            value={supervisorPin}
                                            onChange={(e) => { setSupervisorPin(e.target.value.replace(/\D/g, '')); setError(''); }}
                                            placeholder="••••••"
                                            className="w-full px-4 py-4 border-2 border-slate-200 rounded-2xl text-2xl font-bold text-center tracking-widest focus:ring-4 focus:ring-amber-500/10 focus:border-amber-400 transition-all"
                                            autoFocus
                                            onKeyDown={(e) => { if (e.key === 'Enter') setStep('confirm'); }}
                                        />
                                        {/* PIN Dots */}
                                        <div className="flex items-center justify-center gap-2 mt-3">
                                            {Array.from({ length: 6 }).map((_, i) => (
                                                <div
                                                    key={i}
                                                    className={clsx(
                                                        'w-3 h-3 rounded-full border-2 transition-all',
                                                        i < supervisorPin.length
                                                            ? 'bg-amber-500 border-amber-500'
                                                            : 'border-slate-300'
                                                    )}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* PIN Pad */}
                                    <div className="grid grid-cols-3 gap-2">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, '⌫'].map((key, idx) => (
                                            <motion.button
                                                key={idx}
                                                whileHover={key !== '' ? { scale: 1.05 } : {}}
                                                whileTap={key !== '' ? { scale: 0.95 } : {}}
                                                onClick={() => {
                                                    if (key === '⌫') setSupervisorPin(prev => prev.slice(0, -1));
                                                    else if (key !== '') setSupervisorPin(prev => (prev + key).slice(0, 6));
                                                }}
                                                disabled={key === ''}
                                                className={clsx(
                                                    'py-3.5 rounded-xl text-lg font-bold transition-all select-none',
                                                    key === '' ? 'bg-transparent' :
                                                        key === '⌫' ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200' :
                                                            'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                                                )}
                                                type="button"
                                            >
                                                {key === '⌫' ? '⌫' : key}
                                            </motion.button>
                                        ))}
                                    </div>

                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700"
                                        >
                                            <AlertCircle size={16} className="flex-shrink-0" />
                                            {error}
                                        </motion.div>
                                    )}

                                    <div className="flex gap-3 pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep('reason')}
                                            className="flex-1 py-3.5 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-1"
                                            type="button"
                                        >
                                            <ChevronLeft size={16} />
                                            Back
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep('confirm')}
                                            disabled={supervisorPin.length < 4}
                                            className="flex-1 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-amber-500/25 hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                            type="button"
                                        >
                                            Review
                                            <ChevronRight size={16} />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}

                            {step === 'confirm' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="space-y-4"
                                >
                                    <div className="text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-3">
                                            <AlertCircle size={28} className="text-rose-600" />
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900">Confirm Void</h3>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Are you sure you want to void this sale?
                                        </p>
                                    </div>

                                    {/* Summary */}
                                    <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Order</span>
                                            <span className="font-mono font-bold text-slate-700">{orderNumber}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Amount</span>
                                            <span className="font-bold text-slate-700">{formatNaira(saleTotal)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Reason</span>
                                            <span className="font-medium text-slate-700">{getFinalReason()}</span>
                                        </div>
                                        {requiresSupervisor && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Authorization</span>
                                                <span className="font-medium text-emerald-600 flex items-center gap-1">
                                                    <CheckCircle size={12} />
                                                    Supervisor Verified
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700"
                                        >
                                            <AlertCircle size={16} className="flex-shrink-0" />
                                            {error}
                                        </motion.div>
                                    )}

                                    <div className="flex gap-3 pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setStep(requiresSupervisor ? 'pin' : 'reason')}
                                            className="flex-1 py-3.5 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all flex items-center justify-center gap-1"
                                            type="button"
                                        >
                                            <ChevronLeft size={16} />
                                            Back
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleVoid}
                                            disabled={isProcessing}
                                            className="flex-1 py-3.5 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-rose-500/25 hover:from-rose-600 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                            type="button"
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                        className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                                                    />
                                                    Voiding...
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCcw size={16} />
                                                    Confirm Void
                                                </>
                                            )}
                                        </motion.button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}